import React, { useCallback } from 'react'
import { SubmitHandler, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { ControlledCheckbox, ControlledDatePicker, ControlledSelect, ControlledTextField } from '@rent/ui'
import { FormInputFieldWithProperty } from 'models'

type FormGeneratorProps = {
  onSubmitForm?: SubmitHandler<any>
} & FormInputFieldWithProperty

const FormFieldGenerator = ({ fieldName, properties, onSubmitForm }: FormGeneratorProps) => {
  const intl = useIntl()

  const { control, handleSubmit } = useFormContext()
  const renderField = useCallback(
    (field: FormInputFieldWithProperty) => {
      const {
        properties: { customLabel, customPlaceholder, ...restProperties },
      } = field
      const isDisabled = field.properties.inputProps?.disabled || field.properties.disabled

      const label = customLabel
        ? intl.formatMessage({ id: customLabel.id }, customLabel.values)
        : intl.formatMessage({ id: `form.${field.fieldName}_label` })

      const getPlaceholder = (): string =>
        customPlaceholder
          ? String(intl.formatMessage({ id: customPlaceholder.id }, customPlaceholder.values))
          : (intl.formatMessage({ id: `form.${field.fieldName}_placeholder` }) as string)

      const labelExpanded = () =>
        field.properties.customLabelExpanded
          ? intl.formatMessage({ id: field.properties.customLabelExpanded.id }, field.properties.customLabelExpanded.values)
          : intl.formatMessage({ id: `form.${field.fieldName}_label_expanded` })

      switch (field.properties.type) {
        case 'checkbox':
          return (
            <ControlledCheckbox
              fieldName={field.fieldName}
              label={label}
              required={field.properties?.required}
              control={control}
              {...field.properties}
              type="checkbox"
            />
          )
        case 'checkbox-accordion':
          return (
            <ControlledCheckbox
              fieldName={field.fieldName}
              label={label}
              required={field.properties?.required}
              control={control}
              {...field.properties}
              type="checkbox-accordion"
              labelExpanded={labelExpanded()}
            />
          )
        case 'select':
          return (
            <ControlledSelect
              fieldName={field.fieldName}
              inputProps={{
                ...field.properties.inputProps,
                name: field.fieldName,
              }}
              label={label}
              helperText={field.properties.helperText ? intl.formatMessage({ id: `form.${field.properties.helperText}` }) : ''}
              placeholder={isDisabled ? '' : getPlaceholder()}
              control={control}
              options={field.properties.options ?? []}
              {...field.properties}
            />
          )
        case 'datepicker':
          return (
            <ControlledDatePicker
              fieldName={field.fieldName}
              label={label}
              helperText={field.properties.helperText ? intl.formatMessage({ id: `form.${field.properties.helperText}` }) : ''}
              control={control}
              onPressEnter={typeof onSubmitForm === 'function' ? handleSubmit(onSubmitForm) : undefined}
              {...field.properties}
            />
          )
        default:
          return (
            <ControlledTextField
              fieldName={field.fieldName}
              inputProps={{
                ...field.properties.inputProps,
                name: field.fieldName,
              }}
              label={label}
              helperText={field.properties.helperText ? intl.formatMessage({ id: `form.${field.properties.helperText}` }) : ''}
              placeholder={isDisabled ? '' : getPlaceholder()}
              type={field.properties.type}
              control={control}
              onPressEnter={typeof onSubmitForm === 'function' ? handleSubmit(onSubmitForm) : undefined}
              {...restProperties}
            />
          )
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [control, intl, properties, onSubmitForm],
  )

  return <> {renderField({ fieldName, properties })}</>
}

export default FormFieldGenerator
